import {
  MeterReadingParser,
  LastMeterReadingParser,
} from "@/models/operations/meterReading/meterReading.parser"
import { IMeterReading } from "@/models/operations/meterReading/meterReading.types"

import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { MeterReadingPhotoEvidenceSignedUpGetUrl } from "@/api/backofficeRest/generated/model"
import { QueryResult } from "@/api/utils/types"
import { GetMeterReadingsQueryVariables } from "@/api/hasura/generated/graphql"

import {
  PhotoEvidenceResponseRaw,
  PhotoEvidenceParser,
} from "@/models/meterPhotos/meterPhotos.parser"
import { IMeterReadingPhotoItem } from "@/models/meterPhotos/meterPhotos.types"

import {
  OutboundPhotoRequestResponseRaw,
  OutboundPhotoRequestParser,
} from "@/models/outboundPhoto/outboundPhoto.parser"

import { IOutboundPhotoRequestResponse } from "@/models/outboundPhoto/outboundPhoto.types"
import { Dayjs } from "dayjs"

export type MeterReadingFilters = {
  status?: string[]
  submissionDate: {
    from?: Dayjs
    to?: Dayjs
  }
  errorCode?: string[]
}

export type MeterReadingQueryResult =
  | {
      records: IMeterReading[]
      totalCount: number
    }
  | undefined

class MeterReadingsServiceClass {
  getLastMeterReadings(
    page: number,
    pageSize: number,
  ): QueryResult<MeterReadingQueryResult> {
    return useQuery({
      ...HasuraQueries.meterReadings.lastErroredMeterReadings(
        pageSize,
        (page - 1) * pageSize,
      ),
      useRawResponse: true,
      placeholderData: { records: [], totalCount: 0 },
      parserFn: (rawData) => {
        return {
          records: rawData.last_errored_submissions.map((meterReading) =>
            LastMeterReadingParser.toDomain(meterReading),
          ),
          totalCount:
            rawData.last_errored_submissions_aggregate.aggregate?.count ?? 0,
        }
      },
    })
  }

  getMeterReadings(
    search: string,
    filters: MeterReadingFilters,
    page: number,
    pageSize: number,
  ): QueryResult<MeterReadingQueryResult> {
    const queryProps: GetMeterReadingsQueryVariables = {
      search: `%${search}%`,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }
    if (filters.status?.length) {
      queryProps.status = { _in: filters.status }
    }
    if (filters.submissionDate.from) {
      queryProps.from = filters.submissionDate.from
    }
    if (filters.submissionDate.to) {
      queryProps.to = filters.submissionDate.to
    }
    if (filters.errorCode?.length) {
      queryProps.errorCode = { _in: filters.errorCode }
    }
    return useQuery({
      ...HasuraQueries.meterReadings.erroredMeterReadings(queryProps),
      useRawResponse: true,
      parserFn: (rawData) => {
        if (!rawData) return undefined
        return {
          records: rawData.nova_submissions.map((meterReading) =>
            MeterReadingParser.toDomain(meterReading),
          ),
          totalCount: rawData.nova_submissions_aggregate.aggregate?.count ?? 0,
        }
      },
    })
  }

  getMeterReadingsErrors(): QueryResult<string[]> {
    return useQuery({
      ...HasuraQueries.meterReadings.errors(),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        return rawData.nova_submissions
          .map(({ error_code_1 }) => error_code_1)
          .filter((code): code is string => code != null)
      },
    })
  }

  getPhotoEvidence(
    accountNumber: string,
  ): QueryResult<IMeterReadingPhotoItem[]> {
    return useQuery({
      ...BackofficeRestQueries.meterReadings.photoEvidence(accountNumber),
      useRawResponse: true,
      placeholderData: [] as IMeterReadingPhotoItem[],
      parserFn: (rawData: PhotoEvidenceResponseRaw) =>
        PhotoEvidenceParser.toDomain(rawData),
    })
  }

  getPhotoUrl(imageIdentifier: string) {
    return useQuery({
      ...BackofficeRestQueries.meterReadings.photoUrl(imageIdentifier),
      useRawResponse: true,
      parserFn: (rawData: MeterReadingPhotoEvidenceSignedUpGetUrl) =>
        rawData?.url,
    })
  }

  getOutboundPhotoRequest(
    accountNumber: string,
  ): QueryResult<IOutboundPhotoRequestResponse[]> {
    return useQuery({
      ...HasuraQueries.meterReadings.getOutboundPhotoRequest(accountNumber),
      useRawResponse: true,
      placeholderData: [] as IOutboundPhotoRequestResponse[],
      parserFn: (rawData) =>
        OutboundPhotoRequestParser.toDomain(
          rawData as OutboundPhotoRequestResponseRaw,
        ),
    })
  }
}

export const MeterReadingsService = new MeterReadingsServiceClass()
